import { Page } from '../../base/Page';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useYupValidationResolver } from '../../base/validateResolver';
import { useProducts } from '../../base/useDbHooks';
import { useProductPricing } from '../../base/useDbProductPricingHooks';
import { PreOrder, useBatchProductPricing, usePreOrdersItems } from '../../base/useDbPreOrderHooks';
import { Button, Card, Label, Select, Table, Modal } from 'flowbite-react';
import { useState, useMemo } from 'react';
import { InputText } from '../../base/InputText';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { DataSnapshot } from 'firebase/database';

const formSchema = Yup.object().shape({
  productId: Yup.string().required('Product is required'),
  pricingId: Yup.string().required('Pricing is required'),
  preOrderTime: Yup.string().required('Pre-order time is required'),
});

export default function PreOrderManagement() {
  const { products } = useProducts();
  const { preOrdersItem, addPreOrderItem, removePreOrderItem, loading } = usePreOrdersItems();
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const { productPricingSnapshots } = useProductPricing(selectedProductId);
  const [showRemoveModal, setShowRemoveModal] = useState<string | null>(null);

  // Memoize preOrderProductIds to prevent new array reference on each render
  const preOrderProductIds = useMemo(
    () => Array.from(new Set(preOrdersItem.map((preOrder) => preOrder.productId))),
    [preOrdersItem]
  );

  // Fetch pricing for all pre-order product IDs
  const { pricingSnapshotsMap } = useBatchProductPricing(preOrderProductIds);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PreOrder>({ resolver: useYupValidationResolver(formSchema) });

  const onSubmit = (data: PreOrder) => {
    addPreOrderItem({
      productId: data.productId,
      pricingId: data.pricingId,
      preOrderTime: data.preOrderTime,
    });
    toast.success('Pre-order added');
    reset();
  };

  return (
    <Page title="Pre-Order Management">
      <div className="container mx-auto py-8">
        <h1 className="uppercase text-2xl font-bold text-center mb-6">Manage Pre-Orders</h1>
        <Card className="mb-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div>
              <Label htmlFor="productId" value="Select Product" />
              <Select
                id="productId"
                {...register('productId')}
                onChange={(e) => {
                  setSelectedProductId(e.target.value);
                  setValue('productId', e.target.value);
                }}
              >
                <option value="">Select</option>
                {products.map((product) => (
                  <option key={product.productId} value={product.productId}>
                    {product.productNameEn}
                  </option>
                ))}
              </Select>
              {errors.productId && (
                <span className="text-red-500">{errors.productId.message}</span>
              )}
            </div>
            <div>
              <Label htmlFor="pricingId" value="Select Pricing" />
              <Select id="pricingId" {...register('pricingId')}>
                <option value="">Select</option>
                {productPricingSnapshots
                  .filter((snapshot): snapshot is DataSnapshot & { key: string } => snapshot.key !== null)
                  .map((snapshot) => {
                    const pricing = snapshot.val();
                    return (
                      <option key={snapshot.key} value={snapshot.key}>
                        {pricing.unit} - {pricing.price}円
                      </option>
                    );
                  })}
              </Select>
              {errors.pricingId && (
                <span className="text-red-500">{errors.pricingId.message}</span>
              )}
            </div>
            <div>
              <InputText
                {...register('preOrderTime')}
                name="preOrderTime"
                type="datetime-local"
                label="Pre-Order Time"
                error={errors.preOrderTime?.message}
              />
            </div>
            <Button type="submit" className="bg-blue-500 hover:bg-blue-600">
              Add Pre-Order
            </Button>
          </form>
        </Card>

        <Card>
          <h2 className="text-xl font-bold mb-4">Pre-Order List</h2>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <Table>
              <Table.Head>
                <Table.HeadCell>Product</Table.HeadCell>
                <Table.HeadCell>Pricing</Table.HeadCell>
                <Table.HeadCell>Pre-Order Time</Table.HeadCell>
                <Table.HeadCell>Actions</Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {preOrdersItem.map((preOrder) => {
                  const product = products.find((p) => p.productId === preOrder.productId);
                  const pricingSnapshots = pricingSnapshotsMap.get(preOrder.productId) || [];
                  const pricing = pricingSnapshots
                    .filter((snapshot): snapshot is DataSnapshot & { key: string } => snapshot.key !== null)
                    .find((snapshot) => snapshot.key === preOrder.pricingId)?.val();

                  // Validate preOrderTime
                  const isValidDate = (dateString: string) => {
                    const date = new Date(dateString);
                    return date instanceof Date && !isNaN(date.getTime());
                  };

                  const preOrderDate = preOrder.preOrderTime && isValidDate(preOrder.preOrderTime)
                    ? format(new Date(preOrder.preOrderTime), 'PPp')
                    : 'Invalid Date';

                  return (
                    <Table.Row key={preOrder.preOrderId}>
                      <Table.Cell>{product?.productNameEn || 'Unknown'}</Table.Cell>
                      <Table.Cell>
                        {pricing ? `${pricing.unit} - ${pricing.price}円` : 'Loading...'}
                      </Table.Cell>
                      <Table.Cell>{preOrderDate}</Table.Cell>
                      <Table.Cell>
                        <Button
                          color="failure"
                          onClick={() => setShowRemoveModal(preOrder.preOrderId)}
                        >
                          Delete
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          )}
          <Modal
            show={!!showRemoveModal}
            onClose={() => setShowRemoveModal(null)}
            size="md"
            popup
          >
            <Modal.Header />
            <Modal.Body>
              <div className="text-center">
                <h3 className="mb-5 text-lg font-normal text-gray-500">
                  Are you sure you want to delete this pre-order?
                </h3>
                <div className="flex justify-center gap-4">
                  <Button
                    color="failure"
                    onClick={() => {
                      if (showRemoveModal) {
                        removePreOrderItem(showRemoveModal);
                        toast.success('Pre-order deleted');
                      }
                      setShowRemoveModal(null);
                    }}
                  >
                    Yes, Im sure
                  </Button>
                  <Button color="gray" onClick={() => setShowRemoveModal(null)}>
                    No, cancel
                  </Button>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        </Card>
      </div>
    </Page>
  );
}